import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useDebounce } from "../../../../hooks";
import { getTrxWithdrawalHistory, IGetWithdrawalHistoryData } from "../../../../services/trxWithdrawalService";

export interface FilterI {
  company?: {
    label: string,
    value: string,
  },
  companyRelation?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  codeType?: {
    label: string,
    value: string,
  },
  areaId?: string,
  date?: string,
  search?: string,
}

const useWithdrawalHistory = () => {
  const [searchText, setSearchText] = useState("");
  const [expandedHistoryId, setExpandedHistoryId] = useState("");
  const [
    withdrawalHistoryList,
    setWithdrawalHistoryList,
  ] = useState<IGetWithdrawalHistoryData[]>([]);
  const [params, setParams] = useState<FilterI>({});
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  const [tempHistoryParams, setTempHistoryParams] = useState(params);
  const [paramsCount, setParamsCount] = useState(0);

  useEffect(() => {
    setParamsCount(
      Object.entries(params)
        .filter(([key, val]) => (
          !["search", "areaId"].includes(key)
          && val !== undefined
        )).length,
    );
  }, [params]);

  const fetchList = async (pageParam: number) => {
    const hasRequiredParams = !!(
      params.codeType?.value
      && params.areaId
      && params.pool?.value
      && params.company?.value
      && params.date
    );

    return hasRequiredParams
      ? getTrxWithdrawalHistory({
        area_id: params.areaId,
        company_code: params.company?.value,
        company_relations: params.companyRelation?.value,
        search: params.search,
        page_number: pageParam,
        page_size: 10,
        pool_id: params.pool?.value,
        code_type: params.codeType?.value,
        period_date: params.date,
      })
        .then((res) => {
          if (res.metadata) setMeta(res.metadata);
          return res.list_trx_withdrawal_history;
        })
        .catch(() => [])
      : [];
  };

  const historyQuery = useInfiniteQuery(
    ["withdrawalHistoryList", params],
    async ({ queryKey, pageParam = 1 }) => fetchList(pageParam),
    {
      getNextPageParam: () => (meta.page < meta.total_pages ? meta.page + 1 : undefined),
      keepPreviousData: true,
    },
  );

  const {
    data: withdrawalHistoryPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,
  } = historyQuery;

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));
  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  useEffect(() => {
    setWithdrawalHistoryList(withdrawalHistoryPages?.pages.flatMap((item) => item));
  }, [withdrawalHistoryPages]);

  return {
    historyObsRef: obsRef,
    historyTableRef: tableRef,
    historyMeta: meta,
    searchHistory: searchText,
    setSearchHistory: setSearchText,
    expandedHistoryId,
    setExpandedHistoryId,
    historyParams: params,
    setHistoryParams: setParams,
    historyParamsCount: paramsCount,
    tempHistoryParams,
    setTempHistoryParams,
    historyQuery,
    withdrawalHistoryList,
  };
};

export default useWithdrawalHistory;
